import { Badge, Button, Card, CardContent, cn, Modal, Spinner } from '@crm/ui';
import { Camera, KeyRound, Palette, ShieldCheck, User } from 'lucide-react';
import { useRef, useState } from 'react';
import { Avatar, PageHeader, reduzirImagem } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useSession, useUpdateProfile } from '@/modules/auth/hooks';
import { roleLabel } from '@/modules/auth/utils';
import { ChangePasswordForm } from '../components/change-password-form';
import { ProfileForm } from '../components/profile-form';
import { ThemeSelector } from '../components/theme-selector';

const PASSWORD_FORM_ID = 'change-password-form';

type Aba = 'dados' | 'acesso' | 'preferencias';

const ABAS: { key: Aba; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'dados', label: 'Dados pessoais', icon: User },
  { key: 'acesso', label: 'Acesso', icon: ShieldCheck },
  { key: 'preferencias', label: 'Preferencias', icon: Palette },
];

export function ProfilePage() {
  useDocumentTitle('Meu perfil');

  const { user } = useSession();
  const updateProfile = useUpdateProfile();
  const arquivoRef = useRef<HTMLInputElement>(null);
  const [aba, setAba] = useState<Aba>('dados');
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);

  /** A foto sobe sozinha: escolher e o gesto, salvar seria um passo a mais. */
  async function trocarFoto(arquivo: File | undefined) {
    if (!arquivo) return;

    setEnviando(true);
    try {
      updateProfile.mutate({ image: await reduzirImagem(arquivo) });
    } finally {
      setEnviando(false);
    }
  }

  if (!user) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader icon={<User />} title="Meu perfil" />

      {/* Quem esta logado, em uma faixa: a foto grande, o nome e o e-mail que
          identifica a conta. O resto da tela e o que da para mudar. */}
      <Card variant="flush">
        <CardContent className="flex flex-wrap items-center gap-4">
          <div className="relative shrink-0">
            <Avatar
              name={user.name}
              image={user.image}
              email={user.email}
              className="h-20 w-20 text-2xl"
            />

            {/* Trocar a foto no proprio lugar em que ela aparece: com um
                segundo controle no formulario, ficava a duvida de qual das
                duas imagens valia. */}
            <button
              type="button"
              title="Trocar foto"
              disabled={enviando}
              onClick={() => arquivoRef.current?.click()}
              className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-60"
            >
              {enviando ? <Spinner className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </button>

            <input
              ref={arquivoRef}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(event) => {
                void trocarFoto(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
          </div>

          <div className="min-w-0">
            <p className="truncate text-lg font-semibold">{user.name}</p>
            <p className="truncate text-sm text-muted-foreground">{user.email}</p>
            <Badge variant="secondary" className="mt-1">
              {roleLabel(user.role)}
            </Badge>
          </div>

          {user.image ? (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto text-muted-foreground"
              onClick={() => updateProfile.mutate({ image: '' })}
            >
              Remover foto
            </Button>
          ) : null}
        </CardContent>
      </Card>

      {/* Abas na lateral no monitor e em cima no celular: com tres blocos
          empilhados, o que interessa ficava sempre depois de uma rolagem. */}
      <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <nav
          aria-label="Secoes do perfil"
          className="no-scrollbar -mx-3 flex gap-1 overflow-x-auto px-3 sm:mx-0 sm:px-0 lg:flex-col"
        >
          {ABAS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-current={aba === item.key}
              onClick={() => setAba(item.key)}
              className={cn(
                'flex shrink-0 items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors lg:w-full',
                aba === item.key
                  ? 'bg-primary/10 text-primary'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden />
              {item.label}
            </button>
          ))}
        </nav>

        <Card variant="flush">
          <CardContent className="space-y-4">
            {aba === 'dados' ? <ProfileForm user={user} /> : null}

            {aba === 'acesso' ? (
              <>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Perfis de acesso</p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.profiles.length > 0 ? (
                      user.profiles.map((profile) => (
                        <Badge key={profile.id} variant="outline">
                          {profile.name}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhum perfil atribuido</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Quem define os perfis e o administrador.
                  </p>
                </div>

                <div className="space-y-2 border-t border-border pt-4">
                  <p className="text-sm font-medium">Senha</p>
                  <p className="text-xs text-muted-foreground">
                    Trocar a senha encerra as demais sessoes.
                  </p>
                  {/* O formulario mora na modal: aberto o tempo todo, tres
                      campos de senha ocupavam a aba sem ninguem trocar nada. */}
                  <Button variant="outline" onClick={() => setTrocandoSenha(true)}>
                    <KeyRound className="h-4 w-4" />
                    Trocar senha
                  </Button>
                </div>
              </>
            ) : null}

            {aba === 'preferencias' ? <ThemeSelector /> : null}
          </CardContent>
        </Card>
      </div>

      <Modal
        open={trocandoSenha}
        onClose={() => setTrocandoSenha(false)}
        title="Trocar senha"
        size="small"
        actions={[
          { label: 'Cancelar', variant: 'ghost', onClick: () => setTrocandoSenha(false) },
          { label: 'Trocar senha', type: 'submit', formId: PASSWORD_FORM_ID },
        ]}
      >
        <ChangePasswordForm
          formId={PASSWORD_FORM_ID}
          hideSubmit
          onChanged={() => setTrocandoSenha(false)}
        />
      </Modal>
    </div>
  );
}
